import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { salesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { printTransaction } from '../lib/printDoc';
import PackingListMenu from '../components/transactions/PackingListMenu';
import { FileText, Edit2, Trash2, Printer, CheckCircle2, Loader2, Cloud } from 'lucide-react';
import AgeBadge from '../components/AgeBadge';

const formatCurrency = (v) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);

const formatDateTime = (iso) =>
    new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

const Drafts = () => {
    const navigate = useNavigate();
    const [docs, setDocs] = useState([]);
    const [filter, setFilter] = useState('all'); // all | draft | quotation
    const [loading, setLoading] = useState(true);

    const fetchDocs = async () => {
        setLoading(true);
        try {
            const { data } = await salesAPI.getDrafts();
            setDocs(data);
        } catch {
            toast.error('Failed to load drafts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDocs();
    }, []);

    const filtered = docs.filter((d) => (filter === 'all' ? true : d.status === filter));

    const handleEdit = (doc) => {
        if (doc.status === 'quotation') {
            navigate(`/quotations/${doc.id}/edit`);
        } else {
            navigate(`/drafts/${doc.id}/edit`);
        }
    };

    const handleDelete = async (doc) => {
        if (!window.confirm(`Delete ${doc.status} ${doc.invoice_number}? This cannot be undone.`)) return;
        try {
            await salesAPI.delete(doc.id);
            toast.success('Deleted');
            fetchDocs();
        } catch {
            toast.error('Failed to delete');
        }
    };

    const handleFinalize = (doc) => {
        // Open editor in finalize mode (user must add payment)
        if (doc.status === 'quotation') {
            navigate(`/quotations/${doc.id}/edit?finalize=1`);
        } else {
            navigate(`/drafts/${doc.id}/edit?finalize=1`);
        }
    };

    return (
        <div data-testid="drafts-page" className="space-y-6 animate-fadeIn">
            <div>
                <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">Drafts &amp; Quotations</h1>
                <p className="text-gray-500 mt-1">
                    Work-in-progress invoices and formal quotations · stock is untouched until finalized
                </p>
            </div>

            <Tabs value={filter} onValueChange={setFilter}>
                <TabsList className="bg-[#F5F5F5]">
                    <TabsTrigger value="all" data-testid="filter-all">All ({docs.length})</TabsTrigger>
                    <TabsTrigger value="draft" data-testid="filter-draft">
                        Drafts ({docs.filter((d) => d.status === 'draft').length})
                    </TabsTrigger>
                    <TabsTrigger value="quotation" data-testid="filter-quotation">
                        Quotations ({docs.filter((d) => d.status === 'quotation').length})
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            <Card className="border-0 shadow-sm">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="text-[#C08478]" size={20} />
                        {filtered.length} document{filtered.length !== 1 ? 's' : ''}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex justify-center py-16">
                            <Loader2 className="animate-spin text-[#C08478]" size={32} />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                            <FileText size={48} className="mb-3 opacity-50" />
                            <p className="text-lg font-medium text-[#1A1A1A]">Nothing here yet</p>
                            <p className="text-sm">Save an invoice for later or create a quotation to see it here.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-[#F5F5F5]">
                                        <TableHead>Number</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Customer</TableHead>
                                        <TableHead className="text-right">Amount</TableHead>
                                        <TableHead>Created</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((d) => (
                                        <TableRow key={d.id} className="hover:bg-[#FAFAFA]" data-testid={`draft-row-${d.id}`}>
                                            <TableCell className="font-mono text-sm font-medium">
                                                <div className="flex flex-col gap-0.5">
                                                    <span>{d.invoice_number}</span>
                                                    {d.source_cloud_order_number && (
                                                        <div className="flex flex-wrap items-center gap-1">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    // Send admin to the originating cloud order so
                                                                    // they can re-read the customer's notes / context
                                                                    // before re-quoting. Searches the all-tab by
                                                                    // order number so the row surfaces regardless
                                                                    // of its current status.
                                                                    navigate(`/cloud-orders?q=${encodeURIComponent(d.source_cloud_order_number)}`);
                                                                }}
                                                                title="Originated from a B2B portal order — click to open"
                                                                data-testid={`source-chip-${d.id}`}
                                                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[1.5px] font-sans text-[#C08478] hover:text-[#1A1A1A] bg-[#FBF8F6] border border-[#EFE5E1] hover:border-[#C08478] rounded-full px-2 py-0.5 w-fit transition-colors"
                                                            >
                                                                <Cloud size={9} />
                                                                B2B · {d.source_cloud_order_number}
                                                            </button>
                                                            <AgeBadge
                                                                iso={d.source_cloud_order_created_at}
                                                                testid={`age-badge-${d.id}`}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {d.status === 'quotation' ? (
                                                    <Badge className="bg-[#8B5CF6] text-white text-[10px]">QUOTATION</Badge>
                                                ) : (
                                                    <Badge className="bg-[#C08478] text-white text-[10px]">DRAFT</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <p className="font-medium text-[#1A1A1A]">{d.company_name || '-'}</p>
                                                {d.contact_person && (
                                                    <p className="text-xs text-gray-500">{d.contact_person}</p>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right font-bold euro-amount">
                                                {formatCurrency(d.amount)}
                                            </TableCell>
                                            <TableCell className="text-gray-600 text-sm">
                                                {formatDateTime(d.created_at)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        title="Edit"
                                                        onClick={() => handleEdit(d)}
                                                        data-testid={`edit-draft-${d.id}`}
                                                        className="text-gray-400 hover:text-[#C08478]"
                                                    >
                                                        <Edit2 size={16} />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        title="Print"
                                                        onClick={() => printTransaction(d, { language: d.customer_document_language || 'fr' })}
                                                        data-testid={`print-draft-${d.id}`}
                                                        className="text-gray-400 hover:text-[#1A1A1A]"
                                                    >
                                                        <Printer size={16} />
                                                    </Button>
                                                    <PackingListMenu transactionId={d.id} compact testIdPrefix="pkg-draft" />
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        title="Finalize to Invoice"
                                                        onClick={() => handleFinalize(d)}
                                                        data-testid={`finalize-draft-${d.id}`}
                                                        className="text-gray-400 hover:text-[#2A9D8F]"
                                                    >
                                                        <CheckCircle2 size={16} />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        title="Delete"
                                                        onClick={() => handleDelete(d)}
                                                        data-testid={`delete-draft-${d.id}`}
                                                        className="text-gray-400 hover:text-red-500"
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Drafts;
